import React, { useContext, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Icon } from 'semantic-ui-react';
import { TopBarContext, MobileNavButtonDescriptor } from '@/common/layouts/AuthLayout/TopBarContext';
import { FileTypes } from '@/common/constants/filetypes';
import { useGetFileUploadQuery } from '@/areas/settings/repository-management/api/repositoryApi';
import {
  MobilePageContent,
  MobilePageSection,
  Title,
  SectionHeader,
  SectionDescription,
  BodyText,
} from '@/mobile/layouts/common/MobilePageStyles';

const MobileResourceDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams<{ fileId: string }>();
  const { setNavButton } = useContext(TopBarContext);
  const fileId = params.fileId ?? '';

  const { data: resource, isLoading, error } = useGetFileUploadQuery(fileId, {
    skip: !fileId,
  });

  useEffect(() => {
    const closeNavButton: MobileNavButtonDescriptor = {
      type: 'close',
      onClick: () => navigate(-1),
    };

    setNavButton(closeNavButton);

    return () => {
      setNavButton(null);
    };
  }, [navigate, setNavButton]);

  if (!fileId) {
    return (
      <MobilePageContent>
        <MobilePageSection>
          <Title>Resource not found</Title>
          <BodyText>The resource you requested could not be found.</BodyText>
        </MobilePageSection>
      </MobilePageContent>
    );
  }

  return (
    <MobilePageContent>
      <MobilePageSection padbottom>
        <SectionHeader>
          <Title>{resource?.name || 'Resource details'}</Title>
        </SectionHeader>
      </MobilePageSection>

      <MobilePageSection>
        {isLoading ? (
          <LoadingWrapper>
            <Icon name="spinner" loading size="large" />
          </LoadingWrapper>
        ) : error ? (
          <BodyText>Unable to load resource details. Please try again.</BodyText>
        ) : (
          <DetailGrid>
            {resource?.type === FileTypes.URL && resource?.object_key ? (
              <DetailRow>
                <DetailLabel>Link</DetailLabel>
                <DetailLink
                  href={resource.object_key}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={resource.object_key}
                >
                  {resource.object_key}
                </DetailLink>
              </DetailRow>
            ) : null}
            <DetailRow>
              <DetailLabel>Description</DetailLabel>
              <DetailValue>{resource?.description || '—'}</DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>Themes</DetailLabel>
              <DetailValue>{resource?.tags || '—'}</DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>Source</DetailLabel>
              <DetailValue>{resource?.source || '—'}</DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>Outcomes</DetailLabel>
              <DetailValue>{resource?.outcomes || '—'}</DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>AI Overview</DetailLabel>
              <DetailValue>{resource?.ai_overview || '—'}</DetailValue>
            </DetailRow>
          </DetailGrid>
        )}
      </MobilePageSection>
    </MobilePageContent>
  );
};

export default MobileResourceDetailsPage;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 40px 0;
`;

const DetailGrid = styled.div`
  display: grid;
  gap: 16px;
`;

const DetailRow = styled.div`
  display: grid;
  gap: 8px;
`;

const DetailLabel = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
`;

const DetailValue = styled.div`
  font-size: 14px;
  color: #111827;
  line-height: 1.5;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;
`;

const DetailLink = styled.a`
  display: inline-block;
  font-size: 14px;
  color: #2563eb;
  line-height: 1.5;
  text-decoration: underline;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;
`;
